import React, { useMemo, useRef, useState } from 'react';
import styled, { useTheme } from 'styled-components';
import { useTranslation } from 'react-i18next';

import PngDownloadButton from './PngDownloadButton';
import { downloadSvgAsPng } from '../../../utils/svgExport';

// Task 14 — Karşılaştırma sayfasının funnel plot'u (Spiegelhalter 2005 kontrol limitleri).
// Modülün bilimsel iddiasının görsel karşılığı: "devir oranı en yüksek lokasyon" sıralaması
// küçük lokasyonları sistematik olarak tepeye çıkarır (8 kişilik şubede 2 ayrılış %25,
// 200 kişilik merkezde 20 ayrılış %10) — bu yüzden yatay eksene PAYDAYI (grup büyüklüğü)
// koyup %95/%99,8 kontrol limitlerini o paydaya göre çiziyoruz: bir nokta ancak KENDİ
// büyüklüğü için beklenen değişkenliğin dışına çıktığında dikkat çeker.
//
// Backend (HaMetricsDataAccess.GetFunnelAsync → HaFunnelCellResponse) ZATEN her hücre için
// ShrunkValue + dört kontrol limitini (CtrlLimitLower95/Upper95/Lower998/Upper998) hesaplayıp
// gönderiyor (bkz. FunnelPlotCalculator, TurnoverFunnelComputer) — burada bu istatistik
// YENİDEN hesaplanmıyor, yalnızca zaten hesaplanmış değerler ÇİZİLİYOR ve sınıflandırma için
// backend'in FunnelPlotCalculator.Evaluate'indeki AYNI karşılaştırma (value > upper / < lower)
// tekrarlanıyor (classifyFunnelSignal) — bu bir istatistik tanımı değil, bir görüntüleme kararı.
//
// K-anonimlik: bastırılmış bir hücre TÜM sayısal alanları (Value dahil) null olarak gelir
// (bkz. HaFunnelCellResponse yorumu). Böyle bir hücre HİÇ ÇİZİLMEZ — 0'da bir nokta çizmek
// o grup için yanlış bir önerme olurdu (brief). Bkz. isPlottable.
//
// "Hedef çizgisi": backend'in kontrol limitlerini hesaplarken kullandığı gerçek hedef oran
// artık HaFunnelCellResponse.Target alanında GELİYOR (Görev 14 fix round 1) — istemci tarafında
// YENİDEN HESAPLANMIYOR. Önceki sürüm burada yalnız GÖRÜNÜR (bastırılmamış) hücrelerden bir
// yaklaşıksı hesaplıyordu; review bunun YANLIŞ olabileceğini kanıtladı: çok sayıda küçük
// bastırılmış grup + tek bir görünür büyük grup varken (ör. 20×N=3 bastırılmış + 1×N=500
// görünür) istemci hedefi gerçek havuzlanmış orandan belirgin biçimde sapıyor ve görünür
// noktanın KENDİ (gerçek hedefle hesaplanmış) %95 bandının DIŞINA bir çizgi çiziyordu —
// istatistiksel dürüstlük aracı kendiyle çelişiyordu. Artık backend'den gelen alan doğrudan
// kullanılıyor; alan yoksa (eski API/tek gruplu kohort — bkz. HaMetricsDataAccess.
// ComputeTargetByPeriod) çizgi HİÇ ÇİZİLMİYOR — yanlış bir yaklaşıksı yerine dürüst bir yokluk.
const VIEW_WIDTH = 640;
const DEFAULT_HEIGHT = 320;
const MARGIN = { top: 24, right: 28, bottom: 44, left: 56 };

const HIT_AREA_RADIUS = 12; // dataviz kuralı: nokta isabet alanı ≥24px çap.
const SEVERITY_RADIUS = { normal: 5, warn: 6, alarm: 7 };

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
`;

const EmptyState = styled.div`
  padding: 32px 16px;
  text-align: center;
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  color: ${({ theme }) => theme.colors.text.muted};
`;

const Legend = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  padding: 0 4px;
`;

const ToolbarRow = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: ${({ theme }) => theme.metrics.font.size.xs};
  color: ${({ theme }) => theme.colors.text.muted};
`;

const LegendSwatch = styled.span`
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: ${({ $color }) => $color};
  flex-shrink: 0;
`;

// Backend'in FunnelPlotCalculator.Evaluate'indeki sinyal sınıflandırmasıyla BİREBİR aynı
// karşılaştırma yönü (value > upper / value < lower, sınırın kendisi ASLA yeniden hesaplanmaz).
// Bastırılmış (value null) ya da eksik bir hücrede null döner — çağıran taraf bunu "çizme"
// olarak yorumlar.
export function classifyFunnelSignal(cell) {
  if (!cell || cell.value === null || cell.value === undefined) return null;
  const { value, ctrlLimitLower95, ctrlLimitUpper95, ctrlLimitLower998, ctrlLimitUpper998 } = cell;
  if (ctrlLimitUpper998 != null && value > ctrlLimitUpper998) return 'aboveAlarm';
  if (ctrlLimitUpper95 != null && value > ctrlLimitUpper95) return 'aboveWarn';
  if (ctrlLimitLower998 != null && value < ctrlLimitLower998) return 'belowAlarm';
  if (ctrlLimitLower95 != null && value < ctrlLimitLower95) return 'belowWarn';
  return 'normal';
}

function severityOf(signal) {
  if (signal === 'aboveAlarm' || signal === 'belowAlarm') return 'alarm';
  if (signal === 'aboveWarn' || signal === 'belowWarn') return 'warn';
  return 'normal';
}

// Brief: "value=null olan hücre grafikte nokta üretmemeli — 0'da nokta çizmek yalan olur."
// Falsy kontrolü DEĞİL (SmartStatCard'ın "value 0 iken sıfırı yutmaz" dersiyle aynı tuzak) —
// yalnızca null/undefined kontrolü. Denominator da aynı ternary ile birlikte null'landığı için
// (HaMetricsDataAccess.MapFunnelCell) x ekseni konumu için de aynı korumaya ihtiyaç var.
function isPlottable(cell) {
  return (
    !!cell &&
    cell.value !== null && cell.value !== undefined &&
    cell.denominator !== null && cell.denominator !== undefined
  );
}

function linearScale(domainMax, rangeMin, rangeMax) {
  const safeMax = domainMax > 0 ? domainMax : 1;
  return (v) => rangeMin + (Math.max(0, Math.min(v, safeMax)) / safeMax) * (rangeMax - rangeMin);
}

function buildTicks(max, count) {
  return Array.from({ length: count + 1 }, (_, i) => (max * i) / count);
}

// Sıralı noktaları düz çizgi parçalarıyla birleştirir (kontrol limiti "eğrisi"nin ayrık
// yaklaşıksı) — tek nokta varken bir "eğri" anlamsızdır, null döner (hiç çizilmez).
function toPolylinePoints(points, xFn, yFn) {
  if (points.length < 2) return null;
  return points.map((p) => `${xFn(p)},${yFn(p)}`).join(' ');
}

export default function FunnelPlot({ cells, height = DEFAULT_HEIGHT }) {
  const { t, i18n } = useTranslation();
  const theme = useTheme();

  // Kullanıcı geri bildirimi (2026-08-03): "noktanın dikeyde çizgileri nerede kestiğini nasıl
  // anlayacağım? kılavuz çizgisi yok." Üzerine gelinen/odaklanan nokta için eksenlere inen
  // kesikli kılavuz çizgileri çizilir — kıyası göz kararı yapmak zorunda kalınmasın.
  const [hoveredId, setHoveredId] = useState(null);

  const svgRef = useRef(null);

  const percentFormatter = useMemo(
    () => new Intl.NumberFormat(i18n.language, { style: 'percent', maximumFractionDigits: 1 }),
    [i18n.language]
  );
  const countFormatter = useMemo(() => new Intl.NumberFormat(i18n.language), [i18n.language]);

  const points = useMemo(
    () => (cells || [])
      .filter(isPlottable)
      .map((cell) => ({ cell, signal: classifyFunnelSignal(cell) }))
      .sort((a, b) => a.cell.denominator - b.cell.denominator),
    [cells]
  );

  if (points.length === 0) {
    return <EmptyState>{t('hrAnalytics.funnelPlot.empty')}</EmptyState>;
  }

  const innerWidth = VIEW_WIDTH - MARGIN.left - MARGIN.right;
  const innerHeight = height - MARGIN.top - MARGIN.bottom;

  const xDomainMax = (Math.max(...points.map((p) => p.cell.denominator)) * 1.08) || 1;
  const yValues = points.flatMap((p) => [
    p.cell.value,
    p.cell.ctrlLimitUpper998 ?? p.cell.value,
    p.cell.ctrlLimitUpper95 ?? p.cell.value,
  ]);
  const yDomainMax = (Math.max(...yValues, 0.01)) * 1.15;

  const xScale = linearScale(xDomainMax, 0, innerWidth);
  const x = (v) => MARGIN.left + xScale(v);
  const yScale = linearScale(yDomainMax, 0, innerHeight);
  const y = (v) => MARGIN.top + innerHeight - yScale(v);

  // Hedef çizgisi — bkz. dosya başı yorumu. Backend'in gönderdiği Target alanı doğrudan
  // kullanılır (görünür noktalar aynı dönem/kapsam kohortundan geldiği için hepsi AYNI değeri
  // taşır — ilk dolu olanı yeterli). Hiçbir nokta Target taşımıyorsa (eski API ya da tek
  // gruplu kohort) çizgi HİÇ çizilmez — yaklaşıksı ÜRETİLMEZ.
  const targetPoint = points.find((p) => p.cell.target !== null && p.cell.target !== undefined);
  const target = targetPoint ? targetPoint.cell.target : null;

  const xTicks = buildTicks(xDomainMax, 4);
  const yTicks = buildTicks(yDomainMax, 4);

  const curve998Points = points.filter((p) => p.cell.ctrlLimitUpper998 != null && p.cell.ctrlLimitLower998 != null);
  const curve95Points = points.filter((p) => p.cell.ctrlLimitUpper95 != null && p.cell.ctrlLimitLower95 != null);

  const upper998Path = toPolylinePoints(curve998Points, (p) => x(p.cell.denominator), (p) => y(p.cell.ctrlLimitUpper998));
  const lower998Path = toPolylinePoints(curve998Points, (p) => x(p.cell.denominator), (p) => y(p.cell.ctrlLimitLower998));
  const upper95Path = toPolylinePoints(curve95Points, (p) => x(p.cell.denominator), (p) => y(p.cell.ctrlLimitUpper95));
  const lower95Path = toPolylinePoints(curve95Points, (p) => x(p.cell.denominator), (p) => y(p.cell.ctrlLimitLower95));

  const SEVERITY_COLOR = {
    normal: theme.colors.auxiliar.success,
    warn: theme.colors.auxiliar.warning,
    alarm: theme.colors.auxiliar.danger,
  };

  // HTML lejantı SVG'nin DIŞINDA yaşadığından PNG dışa aktarımında kaybolurdu — aynı liste
  // export'a da beslenir (svgExport, canvas altına işler).
  const legendItems = [
    { label: t('hrAnalytics.funnelPlot.legendNormal'), color: SEVERITY_COLOR.normal },
    { label: t('hrAnalytics.funnelPlot.legendWarn'), color: SEVERITY_COLOR.warn },
    { label: t('hrAnalytics.funnelPlot.legendAlarm'), color: SEVERITY_COLOR.alarm },
  ];

  const handleDownload = () => {
    downloadSvgAsPng(svgRef.current, {
      fileName: 'devir-karsilastirma.png',
      background: theme.colors.surface.card,
      legend: legendItems,
      legendTextColor: theme.colors.text.primary,
    }).catch(() => {}); // indirme başarısızlığı sessizce yutulur — API hatası değil, toast kuralı dışı
  };

  return (
    <Wrapper>
      <ToolbarRow>
        <PngDownloadButton dataRole="funnel-download-png" onClick={handleDownload} />
      </ToolbarRow>
      <svg
        ref={svgRef}
        viewBox={`0 0 ${VIEW_WIDTH} ${height}`}
        width="100%"
        height={height}
        role="img"
        aria-label={t('hrAnalytics.funnelPlot.chartAriaLabel')}
      >
        {/* eksenler — tema token'ı, ince/düz çizgi (dataviz: gridline hairline, hardcode YOK) */}
        <line
          x1={MARGIN.left} y1={MARGIN.top + innerHeight}
          x2={MARGIN.left + innerWidth} y2={MARGIN.top + innerHeight}
          stroke={theme.colors.surface.border} strokeWidth={1}
        />
        <line
          x1={MARGIN.left} y1={MARGIN.top}
          x2={MARGIN.left} y2={MARGIN.top + innerHeight}
          stroke={theme.colors.surface.border} strokeWidth={1}
        />

        {xTicks.map((tv) => (
          <g key={`xt-${tv}`}>
            <line
              x1={x(tv)} y1={MARGIN.top + innerHeight}
              x2={x(tv)} y2={MARGIN.top + innerHeight + 4}
              stroke={theme.colors.surface.border} strokeWidth={1}
            />
            <text
              x={x(tv)} y={MARGIN.top + innerHeight + 16}
              textAnchor="middle" fontSize={10}
              fontFamily={theme.metrics.font.family} fill={theme.colors.text.muted}
            >
              {countFormatter.format(Math.round(tv))}
            </text>
          </g>
        ))}
        {yTicks.map((tv) => (
          <g key={`yt-${tv}`}>
            <line
              x1={MARGIN.left - 4} y1={y(tv)}
              x2={MARGIN.left} y2={y(tv)}
              stroke={theme.colors.surface.border} strokeWidth={1}
            />
            <text
              x={MARGIN.left - 8} y={y(tv) + 3}
              textAnchor="end" fontSize={10}
              fontFamily={theme.metrics.font.family} fill={theme.colors.text.muted}
            >
              {percentFormatter.format(tv)}
            </text>
          </g>
        ))}

        <text
          x={MARGIN.left + innerWidth / 2} y={height - 6}
          textAnchor="middle" fontSize={11}
          fontFamily={theme.metrics.font.family} fill={theme.colors.text.muted}
        >
          {t('hrAnalytics.funnelPlot.xAxisLabel')}
        </text>
        <text
          x={14} y={MARGIN.top + innerHeight / 2}
          textAnchor="middle" fontSize={11}
          fontFamily={theme.metrics.font.family} fill={theme.colors.text.muted}
          transform={`rotate(-90 14 ${MARGIN.top + innerHeight / 2})`}
        >
          {t('hrAnalytics.funnelPlot.yAxisLabel')}
        </text>

        {/* kontrol limiti "eğrileri" — 99,8% dıştaki (alarm rengi), 95% içteki (uyarı rengi)
            bant; her ikisi de KENDİ statü rengiyle, düşük opaklıkta (referans, veri noktası
            DEĞİL) çizilir, böylece "bu çizginin dışına taşan nokta o statüye girer" okuması
            renk üzerinden de tutarlı olur. */}
        {lower998Path && (
          <polyline points={lower998Path} fill="none" stroke={theme.colors.auxiliar.danger} strokeOpacity={0.35} strokeWidth={2} />
        )}
        {upper998Path && (
          <polyline points={upper998Path} fill="none" stroke={theme.colors.auxiliar.danger} strokeOpacity={0.35} strokeWidth={2} />
        )}
        {lower95Path && (
          <polyline points={lower95Path} fill="none" stroke={theme.colors.auxiliar.warning} strokeOpacity={0.5} strokeWidth={2} />
        )}
        {upper95Path && (
          <polyline points={upper95Path} fill="none" stroke={theme.colors.auxiliar.warning} strokeOpacity={0.5} strokeWidth={2} />
        )}

        {/* hedef çizgisi — kesikli (tek bir referans DEĞERİ, bant sınırı değil). data-role/
            data-target test kancası: backend Target göndermezse bu grup HİÇ render edilmez
            (yaklaşıksı üretilmez, bkz. dosya başı yorumu). */}
        {target !== null && (
          <g data-role="target-line" data-target={target}>
            <line
              x1={MARGIN.left} y1={y(target)}
              x2={MARGIN.left + innerWidth} y2={y(target)}
              stroke={theme.colors.text.muted} strokeWidth={2} strokeDasharray="4 4"
            />
            <text
              x={MARGIN.left + innerWidth} y={y(target) - 6}
              textAnchor="end" fontSize={10}
              fontFamily={theme.metrics.font.family} fill={theme.colors.text.muted}
            >
              {`${t('hrAnalytics.funnelPlot.targetLabel')}: ${percentFormatter.format(target)}`}
            </text>
          </g>
        )}

        {/* hover kılavuzu — seçili noktadan iki eksene inen kesikli çizgiler. Noktalardan
            ÖNCE çizilir ki noktaların altında kalsın. */}
        {points.filter((p) => p.cell.scopeId === hoveredId).map(({ cell }) => (
          <g key={`guide-${cell.scopeId}`} data-role="funnel-hover-guide" pointerEvents="none">
            <line
              x1={x(cell.denominator)} y1={MARGIN.top}
              x2={x(cell.denominator)} y2={MARGIN.top + innerHeight}
              stroke={theme.colors.text.muted} strokeWidth={1} strokeDasharray="3 3" strokeOpacity={0.6}
            />
            <line
              x1={MARGIN.left} y1={y(cell.value)}
              x2={x(cell.denominator)} y2={y(cell.value)}
              stroke={theme.colors.text.muted} strokeWidth={1} strokeDasharray="3 3" strokeOpacity={0.6}
            />
          </g>
        ))}

        {/* nokta bulutu */}
        {points.map(({ cell, signal }) => {
          const severity = severityOf(signal);
          const cx = x(cell.denominator);
          const cy = y(cell.value);
          const r = SEVERITY_RADIUS[severity];
          const color = SEVERITY_COLOR[severity];
          const direction =
            signal === 'aboveWarn' || signal === 'aboveAlarm' ? t('hrAnalytics.funnelPlot.aboveLabel') :
            signal === 'belowWarn' || signal === 'belowAlarm' ? t('hrAnalytics.funnelPlot.belowLabel') :
            null;
          const scopeLabel = t(`hrAnalytics.scopes.${cell.scopeType}`);
          const shortId = String(cell.scopeId || '').slice(0, 8);
          // Backend artık kapsam ADINI gönderiyor (HaFunnelCellResponse.ScopeName) — Guid
          // kırpıntısı yalnız eski API yanıtları için yedek.
          const groupLabel = cell.scopeName || `${scopeLabel} #${shortId}`;
          // Tooltip'e noktanın KENDİ normal aralığı da yazılır — "bu büyüklükte %kaç normal
          // sayılır" sorusunun cevabı çizgi kıyası gerektirmeden okunabilsin.
          const normalRange =
            cell.ctrlLimitLower95 != null && cell.ctrlLimitUpper95 != null
              ? `${t('hrAnalytics.funnelPlot.normalRangeLabel')}: ${percentFormatter.format(cell.ctrlLimitLower95)}–${percentFormatter.format(cell.ctrlLimitUpper95)}`
              : null;
          const tooltipText = [
            groupLabel,
            `${percentFormatter.format(cell.value)} (${t('hrAnalytics.funnelPlot.nLabel')}: ${countFormatter.format(cell.denominator)})`,
            normalRange,
            direction,
          ].filter(Boolean).join(' — ');

          return (
            <g
              key={cell.scopeId}
              data-role="funnel-point"
              data-signal={signal}
              data-in-limit={signal === 'normal' ? 'true' : 'false'}
              data-scope-id={cell.scopeId}
              tabIndex={0}
              role="img"
              aria-label={tooltipText}
              onMouseEnter={() => setHoveredId(cell.scopeId)}
              onMouseLeave={() => setHoveredId(null)}
              onFocus={() => setHoveredId(cell.scopeId)}
              onBlur={() => setHoveredId(null)}
            >
              <title>{tooltipText}</title>
              {/* görünmez, büyütülmüş isabet alanı (dataviz: nokta hedefi ≥24px çap olmalı) */}
              <circle cx={cx} cy={cy} r={HIT_AREA_RADIUS} fill="transparent" />
              <circle cx={cx} cy={cy} r={r} fill={color} stroke={theme.colors.surface.card} strokeWidth={2} />
            </g>
          );
        })}
      </svg>

      <Legend>
        {legendItems.map((item) => (
          <LegendItem key={item.label}>
            <LegendSwatch $color={item.color} />
            {item.label}
          </LegendItem>
        ))}
      </Legend>
    </Wrapper>
  );
}
